import { useEffect, useMemo, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { hasPermission } from "../lib/permissions";
import api, { errorMessage } from "../lib/api";
import { getInitials, avatarColorClass } from "../lib/avatar";
import {
  EVENT_LABELS, RESOURCE_TYPE_LABELS, OUTCOME_LABELS, RESOURCE_TYPE_BADGE_CLASS, OUTCOME_BADGE_CLASS,
  describeActivityEvent, actorDisplayName, actorIsSystem, isViaCopilot,
} from "../lib/activity";
import ViaCopilotBadge from "../components/ViaCopilotBadge";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { ResponsivePopover as Popover, ResponsivePopoverTrigger as PopoverTrigger, ResponsivePopoverContent as PopoverContent } from "../components/ui/responsive-popover";
import { toast } from "sonner";
import { Loader2, Building2, Search, Cog, SlidersHorizontal, X } from "lucide-react";

const PAGE_SIZE = 50;

const PERIOD_OPTIONS = [
  { value: "all", label: "Toutes les dates" },
  { value: "today", label: "Aujourd'hui" },
  { value: "7d", label: "7 derniers jours" },
  { value: "30d", label: "30 derniers jours" },
  { value: "90d", label: "90 derniers jours" },
];

function periodToDateFrom(period) {
  if (period === "all") return null;
  const from = new Date();
  if (period === "today") {
    from.setHours(0, 0, 0, 0);
    return from;
  }
  const days = { "7d": 7, "30d": 30, "90d": 90 }[period];
  from.setDate(from.getDate() - days);
  return from;
}

const DEFAULT_FILTERS = { resourceType: "all", eventType: "all", actorId: "all", outcome: "all", period: "all", via: "all" };
const VIA_OPTIONS = [{ value: "all", label: "Toutes les origines" }, { value: "assistant", label: "Via le Copilote" }];

// Mobile feed grouping: calendar-day buckets with an "Aujourd'hui"/"Hier"/
// explicit-date label. `items` is already sorted newest-first by the API, so
// grouping consecutive same-day entries is enough.
function dayLabel(date, now) {
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return "Aujourd'hui";
  if (diffDays === 1) return "Hier";
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

function groupEventsByDay(events) {
  const now = new Date();
  const groups = [];
  let current = null;
  for (const e of events) {
    const label = dayLabel(new Date(e.occurred_at), now);
    if (!current || current.label !== label) {
      current = { label, events: [] };
      groups.push(current);
    }
    current.events.push(e);
  }
  return groups;
}

export default function Activity() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [users, setUsers] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ resource_types: [], event_types: [], outcomes: [], actor_ids: [] });
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [resourceType, setResourceType] = useState(DEFAULT_FILTERS.resourceType);
  const [eventType, setEventType] = useState(DEFAULT_FILTERS.eventType);
  const [actorId, setActorId] = useState(DEFAULT_FILTERS.actorId);
  const [outcome, setOutcome] = useState(DEFAULT_FILTERS.outcome);
  const [period, setPeriod] = useState(DEFAULT_FILTERS.period);
  const [via, setVia] = useState(DEFAULT_FILTERS.via);

  const hasActiveFilters = search || resourceType !== "all" || eventType !== "all" || actorId !== "all" || outcome !== "all" || period !== "all"
    || via !== "all";

  const usersById = Object.fromEntries(users.map((u) => [u.id, u]));
  const actorIdsWithActivity = new Set(filterOptions.actor_ids);
  const actorOptions = users.filter((u) => actorIdsWithActivity.has(u.id));

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setResourceType(DEFAULT_FILTERS.resourceType);
    setEventType(DEFAULT_FILTERS.eventType);
    setActorId(DEFAULT_FILTERS.actorId);
    setOutcome(DEFAULT_FILTERS.outcome);
    setPeriod(DEFAULT_FILTERS.period);
    setVia(DEFAULT_FILTERS.via);
  };

  // Mobile-only removable chips: desktop already shows the active value
  // directly inside each visible Select.
  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (search) chips.push({ id: "search", label: `Recherche : ${search}`, onRemove: () => { setSearchInput(""); setSearch(""); } });
    if (resourceType !== "all") {
      chips.push({ id: "resource", label: RESOURCE_TYPE_LABELS[resourceType] || resourceType, onRemove: () => setResourceType("all") });
    }
    if (eventType !== "all") {
      chips.push({ id: "event", label: EVENT_LABELS[eventType] || eventType, onRemove: () => setEventType("all") });
    }
    if (actorId !== "all") {
      chips.push({ id: "actor", label: usersById[actorId]?.name || "Utilisateur", onRemove: () => setActorId("all") });
    }
    if (outcome !== "all") {
      chips.push({ id: "outcome", label: OUTCOME_LABELS[outcome] || outcome, onRemove: () => setOutcome("all") });
    }
    if (period !== "all") {
      chips.push({ id: "period", label: PERIOD_OPTIONS.find((p) => p.value === period)?.label || period, onRemove: () => setPeriod("all") });
    }
    if (via !== "all") chips.push({ id: "via", label: "Via le Copilote", onRemove: () => setVia("all") });
    return chips;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, resourceType, eventType, actorId, outcome, period, via, users]);

  const buildParams = (skip) => {
    const params = { limit: PAGE_SIZE, skip };
    if (search) params.search = search;
    if (resourceType !== "all") params.resource_type = resourceType;
    if (eventType !== "all") params.event_type = eventType;
    if (actorId !== "all") params.actor_id = actorId;
    if (outcome !== "all") params.outcome = outcome;
    if (via !== "all") params.via = via;
    const dateFrom = periodToDateFrom(period);
    if (dateFrom) params.date_from = dateFrom.toISOString();
    return params;
  };

  const load = async () => {
    setItems(null);
    try {
      const { data } = await api.get("/activity", { params: buildParams(0) });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de charger le journal d'activité."));
      setItems([]);
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get("/activity", { params: buildParams(items.length) });
      setItems((prev) => [...prev, ...data.items]);
      setTotal(data.total);
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de charger la suite."));
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    api.get("/activity/filters").then(({ data }) => {
      setFilterOptions(data);
      setUsers(data.actors || []);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced, same 300ms convention as GlobalSearch.jsx.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceType, eventType, actorId, outcome, period, via, search]);

  if (!hasPermission(user, "activity:view")) return <Navigate to="/pipeline" replace />;

  return (
    <div className="space-y-4" data-testid="activity-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Journal d'activité</h1>
        <p className="hidden md:block text-sm text-slate-500">Consultez et analysez les actions réalisées sur Jamora CRM.</p>
        {/* `total` is the API's own reliable count (data.total), not just
            items.length (only the currently loaded page). */}
        <p className="md:hidden text-sm text-slate-500">{total} événement{total > 1 ? "s" : ""}</p>
      </div>

      {/* Compact mobile filter bar: search + a single "Filtres" popover
          reusing the exact same resourceType/eventType/actorId/outcome/
          period state as desktop's always-visible selects below. */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              placeholder="Rechercher une activité…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
              data-testid="input-activity-search"
            />
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={resetFilters}
              className="hidden md:inline text-sm text-jamora hover:underline whitespace-nowrap"
              data-testid="btn-reset-filters"
            >
              Réinitialiser
            </button>
          )}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden shrink-0" aria-label="Filtres" data-testid="btn-activity-filters-toggle">
                <SlidersHorizontal size={16} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 space-y-3 md:hidden">
              <div className="text-sm font-semibold text-slate-900">Filtres</div>
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-slate-500">Ressource</div>
                <Select value={resourceType} onValueChange={setResourceType}>
                  <SelectTrigger data-testid="select-resource-type-mobile"><SelectValue placeholder="Toutes les ressources" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les ressources</SelectItem>
                    {filterOptions.resource_types.map((t) => <SelectItem key={t} value={t}>{RESOURCE_TYPE_LABELS[t] || t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-slate-500">Action</div>
                <Select value={eventType} onValueChange={setEventType}>
                  <SelectTrigger data-testid="select-event-type-mobile"><SelectValue placeholder="Toutes les actions" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les actions</SelectItem>
                    {filterOptions.event_types.map((t) => <SelectItem key={t} value={t}>{EVENT_LABELS[t] || t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-slate-500">Acteur</div>
                <Select value={actorId} onValueChange={setActorId}>
                  <SelectTrigger data-testid="select-actor-mobile"><SelectValue placeholder="Tous les acteurs" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les acteurs</SelectItem>
                    {actorOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-slate-500">Résultat</div>
                <Select value={outcome} onValueChange={setOutcome}>
                  <SelectTrigger data-testid="select-outcome-mobile"><SelectValue placeholder="Tous les résultats" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les résultats</SelectItem>
                    {filterOptions.outcomes.map((k) => <SelectItem key={k} value={k}>{OUTCOME_LABELS[k] || k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {filterOptions.has_assistant && (
                <div className="space-y-1.5">
                  <div className="text-xs font-medium text-slate-500">Origine</div>
                  <Select value={via} onValueChange={setVia}>
                    <SelectTrigger data-testid="select-via-mobile"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VIA_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1.5">
                <div className="text-xs font-medium text-slate-500">Date</div>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger data-testid="select-period-mobile"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PERIOD_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline" size="sm" className="w-full"
                onClick={resetFilters} disabled={!hasActiveFilters} data-testid="btn-reset-filters-mobile"
              >
                Réinitialiser
              </Button>
            </PopoverContent>
          </Popover>
        </div>

        {/* Chips scroll horizontally rather than wrap. */}
        {activeFilterChips.length > 0 && (
          <div className="md:hidden flex gap-1.5 overflow-x-auto pb-0.5">
            {activeFilterChips.map((chip) => (
              <button
                key={chip.id} type="button" onClick={chip.onRemove}
                data-testid={`chip-${chip.id}`}
                className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full pl-2.5 pr-1.5 py-1 hover:bg-slate-200 shrink-0 whitespace-nowrap"
              >
                {chip.label} <X size={12} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="hidden md:flex flex-col md:flex-row flex-wrap gap-3">
        <Select value={resourceType} onValueChange={setResourceType}>
          <SelectTrigger className="w-full md:w-52" data-testid="select-resource-type">
            <SelectValue placeholder="Type de ressource" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les ressources</SelectItem>
            {filterOptions.resource_types.map((t) => (
              <SelectItem key={t} value={t}>{RESOURCE_TYPE_LABELS[t] || t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={eventType} onValueChange={setEventType}>
          <SelectTrigger className="w-full md:w-52" data-testid="select-event-type">
            <SelectValue placeholder="Type d'action" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les actions</SelectItem>
            {filterOptions.event_types.map((t) => (
              <SelectItem key={t} value={t}>{EVENT_LABELS[t] || t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={actorId} onValueChange={setActorId}>
          <SelectTrigger className="w-full md:w-52" data-testid="select-actor">
            <SelectValue placeholder="Acteur" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les acteurs</SelectItem>
            {actorOptions.map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={outcome} onValueChange={setOutcome}>
          <SelectTrigger className="w-full md:w-40" data-testid="select-outcome">
            <SelectValue placeholder="Résultat" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les résultats</SelectItem>
            {filterOptions.outcomes.map((k) => (
              <SelectItem key={k} value={k}>{OUTCOME_LABELS[k] || k}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filterOptions.has_assistant && (
          <Select value={via} onValueChange={setVia}>
            <SelectTrigger className="w-full md:w-44" data-testid="select-via">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VIA_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-full md:w-44" data-testid="select-period">
            <SelectValue placeholder="Période" />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {items === null ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-jamora" />
        </div>
      ) : (
        <Card className="p-0 overflow-hidden">
          {items.length === 0 ? (
            <>
              <div className="hidden md:block p-12 text-center text-slate-500 text-sm">Aucune activité ne correspond à ces critères.</div>
              <div className="md:hidden text-center py-12 px-4">
                <p className="text-sm text-slate-500">Aucune activité ne correspond à ces critères.</p>
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" className="mt-3" onClick={resetFilters} data-testid="btn-reset-filters-empty">
                    Réinitialiser les filtres
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date et heure</TableHead>
                  <TableHead>Acteur</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead className="hidden md:table-cell">Ressource</TableHead>
                  <TableHead>Résultat</TableHead>
                  <TableHead className="hidden lg:table-cell">Détails</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow
                    key={e.id}
                    data-testid={`row-activity-${e.id}`}
                    tabIndex={0}
                    role="button"
                    onClick={() => setSelectedEvent(e)}
                    onKeyDown={(ev) => {
                      if (ev.key === "Enter" || ev.key === " ") {
                        ev.preventDefault();
                        setSelectedEvent(e);
                      }
                    }}
                    className="cursor-pointer hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jamora/50"
                  >
                    <TableCell className="text-sm text-slate-500 whitespace-nowrap">
                      {new Date(e.occurred_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <ActorAvatar event={e} usersById={usersById} />
                        <span className="text-sm text-slate-900">{actorDisplayName(e, usersById)}</span>
                        {isViaCopilot(e) && <ViaCopilotBadge />}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-900">{EVENT_LABELS[e.event_type] || e.event_type}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="space-y-1">
                        <Badge variant="outline" className={`border-transparent text-2xs font-medium ${RESOURCE_TYPE_BADGE_CLASS[e.resource_type] || "bg-slate-50 text-slate-600"}`}>
                          {RESOURCE_TYPE_LABELS[e.resource_type] || e.resource_type}
                        </Badge>
                        <div className="text-xs text-slate-500">
                          {e.resource_type === "prospect" && e.resource_id ? (
                            <Link to={`/prospects/${e.resource_id}`} onClick={(ev) => ev.stopPropagation()} className="text-jamora hover:underline">
                              {e.resource_label || "Voir le prospect"}
                            </Link>
                          ) : (
                            e.resource_label || "Sans libellé"
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`border-transparent text-2xs font-medium ${OUTCOME_BADGE_CLASS[e.outcome] || "bg-slate-50 text-slate-600"}`}>
                        {OUTCOME_LABELS[e.outcome] || e.outcome}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-slate-500">
                      {describeActivityEvent(e, { usersById }) || "Aucun détail"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>

            <div className="md:hidden">
              {groupEventsByDay(items).map((group) => (
                <ActivityDayGroup key={group.label} label={group.label} events={group.events} onSelect={setSelectedEvent} usersById={usersById} />
              ))}
            </div>
            </>
          )}
        </Card>
      )}

      {items && items.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-sm text-slate-500">
          <span>Affichage de 1 à {items.length} sur {total} activité{total > 1 ? "s" : ""}</span>
          {items.length < total && (
            <Button variant="outline" onClick={loadMore} disabled={loadingMore} data-testid="btn-load-more">
              {loadingMore ? <Loader2 size={16} className="animate-spin mr-1.5" /> : null}
              Charger plus
            </Button>
          )}
        </div>
      )}

      <ActivityDetailSheet
        event={selectedEvent}
        onOpenChange={(open) => !open && setSelectedEvent(null)}
        usersById={usersById}
        navigate={navigate}
      />
    </div>
  );
}

// `compact` (mobile feed) shrinks the same three states (system/unknown/
// initials) rather than duplicating this logic in a second component.
function ActorAvatar({ event, usersById, compact }) {
  const dim = compact ? "w-5 h-5" : "w-8 h-8";
  const textSize = compact ? "text-2xs" : "text-2xs";
  const iconSize = compact ? 11 : 16;
  if (actorIsSystem(event)) {
    return (
      <div className={`${dim} rounded-full bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0`} title="Système">
        <Cog size={iconSize} />
      </div>
    );
  }
  if (!event.actor_id) {
    return (
      <div className={`${dim} rounded-full bg-slate-100 text-slate-400 flex items-center justify-center ${textSize} font-semibold flex-shrink-0`} title="Inconnu">
        ?
      </div>
    );
  }
  const live = usersById?.[event.actor_id];
  const name = live?.name || event.actor_snapshot?.name;
  const email = live?.email || event.actor_snapshot?.email;
  return (
    <div className={`${dim} rounded-full flex items-center justify-center ${textSize} font-semibold flex-shrink-0 ${avatarColorClass(event.actor_id)}`}>
      {getInitials(name, email)}
    </div>
  );
}

// Mobile feed group: a plain day heading, no rail marker of its own
// (reserved for individual events).
function ActivityDayGroup({ label, events, onSelect, usersById }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-3 pt-3 pb-1">{label}</div>
      {events.map((e, i) => (
        <ActivityFeedItem key={e.id} event={e} isLast={i === events.length - 1} onSelect={onSelect} usersById={usersById} />
      ))}
    </div>
  );
}

// One feed row: time, a small self-contained rail segment (dot + a short line
// to the next row; each row owns its segment so nothing needs pixel alignment
// across components), then the event label, a compact actor line, and
// resource/outcome. Only fields present on the event are rendered. Opens the
// existing ActivityDetailSheet through the same onSelect as the desktop row.
function ActivityFeedItem({ event, isLast, onSelect, usersById }) {
  const time = new Date(event.occurred_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  const resourceLabel = RESOURCE_TYPE_LABELS[event.resource_type] || event.resource_type;
  const resourceLine = event.resource_label ? `${resourceLabel} · ${event.resource_label}` : resourceLabel;
  return (
    <button
      type="button"
      onClick={() => onSelect(event)}
      data-testid={`activity-feed-item-${event.id}`}
      className="w-full text-left flex items-start gap-2 px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0"
    >
      <span className="text-xs text-slate-400 shrink-0 w-9 pt-0.5 text-right">{time}</span>
      <div className="relative w-2 shrink-0 self-stretch flex justify-center">
        {!isLast && <span className="absolute top-0 bottom-0 w-px bg-slate-200" aria-hidden="true" />}
        <span className="relative z-10 mt-1.5 w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1 pb-1">
        <div className="text-sm text-slate-900">{EVENT_LABELS[event.event_type] || event.event_type}</div>
        <div className="mt-0.5 flex items-center gap-1.5">
          <ActorAvatar event={event} usersById={usersById} compact />
          <span className="text-xs text-slate-500 truncate">{actorDisplayName(event, usersById)}</span>
          {isViaCopilot(event) && <ViaCopilotBadge />}
        </div>
        <div className="mt-0.5 flex items-center justify-between gap-2">
          <span className="text-xs text-slate-400 truncate">{resourceLine}</span>
          {event.outcome && (
            <Badge variant="outline" className={`border-transparent text-2xs font-medium shrink-0 ${OUTCOME_BADGE_CLASS[event.outcome] || "bg-slate-50 text-slate-600"}`}>
              {OUTCOME_LABELS[event.outcome] || event.outcome}
            </Badge>
          )}
        </div>
      </div>
    </button>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-2 border-b border-slate-100 last:border-0">
      <div className="text-xs font-medium text-slate-500">{label}</div>
      <div className="col-span-2 text-sm text-slate-900 break-words">{children}</div>
    </div>
  );
}

function prettifyKey(key) {
  const spaced = key.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function MetadataValue({ value }) {
  if (value === null || value === undefined || value === "") return <span className="text-slate-400">(vide)</span>;
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-slate-400">Aucun</span>;
    return <>{value.map((v, i) => <span key={i}>{i > 0 && ", "}<MetadataValue value={v} /></span>)}</>;
  }
  if (typeof value === "object") {
    if ("from" in value && "to" in value) {
      return <span><MetadataValue value={value.from} /> → <MetadataValue value={value.to} /></span>;
    }
    return (
      <div className="space-y-1">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex flex-wrap gap-1.5">
            <span className="text-slate-500">{prettifyKey(k)} :</span> <MetadataValue value={v} />
          </div>
        ))}
      </div>
    );
  }
  return String(value);
}

function ActivityDetailSheet({ event, onOpenChange, usersById, navigate }) {
  return (
    <Sheet open={!!event} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md overflow-y-auto" data-testid="activity-detail-sheet">
        {event && (
          <>
            <SheetHeader>
              <SheetTitle>{EVENT_LABELS[event.event_type] || event.event_type}</SheetTitle>
            </SheetHeader>

            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="outline" className={`border-transparent text-2xs font-medium ${RESOURCE_TYPE_BADGE_CLASS[event.resource_type] || "bg-slate-50 text-slate-600"}`}>
                {RESOURCE_TYPE_LABELS[event.resource_type] || event.resource_type}
              </Badge>
              <Badge variant="outline" className={`border-transparent text-2xs font-medium ${OUTCOME_BADGE_CLASS[event.outcome] || "bg-slate-50 text-slate-600"}`}>
                {OUTCOME_LABELS[event.outcome] || event.outcome}
              </Badge>
            </div>

            <div className="mt-4">
              <DetailRow label="Date et heure">
                {new Date(event.occurred_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "medium" })}
              </DetailRow>
              <DetailRow label="Acteur">
                {actorDisplayName(event, usersById)}
                {event.actor_snapshot?.email && (
                  <div className="text-xs text-slate-500">{event.actor_snapshot.email} {event.actor_snapshot.role ? `· ${event.actor_snapshot.role}` : ""}</div>
                )}
              </DetailRow>
              {isViaCopilot(event) && (
                <DetailRow label="Origine">
                  <div className="flex items-center gap-2"><ViaCopilotBadge /> <span>Copilote Jamora</span></div>
                  <div className="text-xs text-slate-500">
                    Action proposée par le Copilote, approuvée et lancée par {actorDisplayName(event, usersById)}.
                  </div>
                </DetailRow>
              )}
              <DetailRow label="Ressource">
                <div>{event.resource_label || "Sans libellé"}</div>
                {event.resource_id && <div className="text-xs text-slate-400 font-mono break-all">{event.resource_id}</div>}
                {event.resource_type === "prospect" && event.resource_id && (
                  <button
                    type="button"
                    onClick={() => { onOpenChange(false); navigate(`/prospects/${event.resource_id}`); }}
                    className="inline-flex items-center gap-1.5 text-jamora hover:underline mt-1"
                  >
                    <Building2 size={14} /> Voir la fiche
                  </button>
                )}
              </DetailRow>
              {event.prospect_id && event.prospect_id !== event.resource_id && (
                <DetailRow label="Prospect lié">
                  <button
                    type="button"
                    onClick={() => { onOpenChange(false); navigate(`/prospects/${event.prospect_id}`); }}
                    className="inline-flex items-center gap-1.5 text-jamora hover:underline"
                  >
                    <Building2 size={14} /> Voir la fiche
                  </button>
                </DetailRow>
              )}
              <DetailRow label="Détails">
                {describeActivityEvent(event, { usersById }) || <span className="text-slate-400">Aucun détail</span>}
              </DetailRow>
              {event.failure_reason && (
                <DetailRow label="Motif d'échec">{event.failure_reason}</DetailRow>
              )}
              {event.metadata && Object.keys(event.metadata).length > 0 && (
                <DetailRow label="Métadonnées">
                  <MetadataValue value={event.metadata} />
                </DetailRow>
              )}
              <DetailRow label="Adresse réseau observée">{event.ip_address || "Non enregistrée"}</DetailRow>
              <DetailRow label="Navigateur / client">
                <span className="break-words">{event.user_agent || "Non enregistré"}</span>
              </DetailRow>
              {event.request_id && (
                <DetailRow label="ID de requête">
                  <span className="text-xs font-mono break-all">{event.request_id}</span>
                </DetailRow>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
